/**
 * Generate cache key for query hooks
 * @param {string|Array} cacheKeyPrefix Cache key for request group
 * @param {string|Array} cacheKey Unique key for request in given group
 * @param {object} params Hook params
 * @param {string} cacheKeySuffix Unique cache key suffix for hook type
 * @param space
 * @returns {string} Cache key for query hook
 */
export const getQueryCacheKey = (
  cacheKeyPrefix,
  cacheKey = '',
  params = null,
  cacheKeySuffix = '',
  space = null,
) => {
  const key = [];
  if (cacheKeyPrefix)
    key.push(
      ...(Array.isArray(cacheKeyPrefix) ? cacheKeyPrefix : [cacheKeyPrefix]),
    );
  if (cacheKey) key.push(...(Array.isArray(cacheKey) ? cacheKey : [cacheKey]));
  if (params && Object.keys(params).length) key.push(params);
  if (cacheKeySuffix) key.push(cacheKeySuffix);
  if (space) key.push(space);
  return key;
};

/**
 * Invalidate content types queries.
 * If no content type provided, queries for all content types will be invalidated.
 *
 * @param {QueryClient} queryClient - tanstack query client
 * @param {string} contentTypeName - content type name
 * @returns {Promise} promise for query client invalidation
 */
export const invalidateContentTypes = (queryClient, contentTypeName = '') =>
  queryClient.invalidateQueries({
    queryKey: ['content-types'],
    predicate: ({ queryKey }) => {
      if (!contentTypeName) return true;

      const isList = !!queryKey.find((query) => query === 'list');
      const params = queryKey.find(
        (key) => typeof key === 'object' && !!key.contentTypeName,
      );
      return isList || params?.contentTypeName === contentTypeName;
    },
  });

/**
 * Invalidate content object queries.
 * If no content type provided, queries for all objects types will be invalidated.
 * If no id provided, queries for all objects for provided type will be invalidated.
 *
 * @param {QueryClient} queryClient - tanstack query client
 * @param {string} contentTypeName - content type name
 * @returns {Promise} promise for query client invalidation
 */
export const invalidateContentObjects = (
  queryClient,
  contentTypeName = '',
  id = '',
) =>
  queryClient.invalidateQueries({
    queryKey: ['content-objects'],
    predicate: ({ queryKey }) => {
      if (!contentTypeName) return true;

      const isList = !!queryKey.find((query) => query === 'list');
      const params = queryKey.find(
        (key) => typeof key === 'object' && !!key.contentTypeName,
      );
      return (
        params?.contentTypeName === contentTypeName &&
        (isList || !id || params?.id === id)
      );
    },
  });
